/**
 * Light, dark, or whatever the machine says.
 *
 * The UI followed `prefers-color-scheme` and nothing else, which is the right
 * default and a poor only option: a bench monitor in a lit lab and a laptop at
 * night want opposite things from the same account, and the OS setting is a
 * blunt instrument for a page somebody keeps open all day.
 *
 * "auto" is a stored value, not the absence of one. Storing only overrides
 * would make "auto" indistinguishable from "never chose", so a user who picks
 * auto after picking dark could not get back to following the OS.
 */
export type Theme = "light" | "dark" | "auto";

const KEY = "hilc.theme";

export function stored(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" || v === "auto" ? v : "auto";
  } catch {
    // Private mode, or storage disabled. Following the OS is the right thing
    // to fall back to, and a theme control is not worth an exception.
    return "auto";
  }
}

/** What "auto" currently resolves to. */
export function systemIsDark(): boolean {
  return (
    typeof matchMedia === "function" &&
    matchMedia("(prefers-color-scheme: dark)").matches
  );
}

export function resolve(theme: Theme): "light" | "dark" {
  return theme === "auto" ? (systemIsDark() ? "dark" : "light") : theme;
}

/**
 * Put the choice on <html>.
 *
 * `color-scheme` matters as much as the class: it is what makes form controls,
 * scrollbars and the space behind the page follow along. Without it a forced
 * dark page keeps white scrollbars and white select menus.
 */
export function apply(theme: Theme): void {
  const dark = resolve(theme) === "dark";
  const root = document.documentElement;
  root.classList.toggle("dark", dark);
  root.style.colorScheme = dark ? "dark" : "light";
}

export function store(theme: Theme): void {
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    // Not fatal: the choice applies for this page load and is forgotten.
  }
  apply(theme);
}

/**
 * Re-apply when the OS flips, but only while following it.
 *
 * Without this, a machine that switches at sunset leaves an "auto" page on
 * yesterday's scheme until it is reloaded.
 */
export function watchSystem(getTheme: () => Theme): () => void {
  if (typeof matchMedia !== "function") return () => {};
  const mq = matchMedia("(prefers-color-scheme: dark)");
  const onChange = () => {
    if (getTheme() === "auto") apply("auto");
  };
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}
